import { Box, Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import { StyledTextField } from "../../../../components/forms/styles/formFieldStyles";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import add_rights_image from "./images/add_administrative_rights.png";
import add_OAuth2_Client_image from "./images/add_OAuth2_Client.png";
import add_permissions_role_user_image from "./images/add_permissions_role_user.png";
import add_scope_image from "./images/add_scope.png";
import copy_the_issuer_image from "./images/copy_the_issuer.png";
import add_role_image from "./images/create_role.png";
import add_user_image from "./images/create_user.png";
import deployed_status_image from "./images/deployed_status.png";
import set_token_Lifespan_image from "./images/set_token_Lifespan.png";
import tenant_ID_image from "./images/tenant_ID.png";

const CyberArkIdentityIntegration = () => {
  const { isIntegrated, isIntegratedLoading } =
    useIsIntegrated("cyberark_identity");

  const colors = useColors();
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const [formValues, setFormValues] = useState({
    credentials: {
      issuer: "",
      application_name: "",
      scope: "",
      client_id: "",
      client_secret: "",
      org_id: "",
    },
  });
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerCyberArkIdentityIntegration } = useAPI();

  const onSubmit = (values) => {
    // Save the form values for potential error recovery
    setFormValues(values);
    setLoading(true);
    registerCyberArkIdentityIntegration(values.credentials)
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("CyberArk Identity integration successful");
        // Clear form values on success
        setFormValues({
          credentials: {
            issuer: "",
            application_name: "",
            scope: "",
            client_id: "",
            client_secret: "",
            org_id: "",
          },
        });
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, the Breeze platform will automatically sync with CyberArk Identity to enhance your identity and access management."
            : ""
        }
        image={logos.CyberArkLogo}
        name={"CyberArk Identity"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>
                CyberArk Identity is integrated!
              </Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}
                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Create User">
                  <Typography variant={"body2"}>
                    Login to your CyberArk Identity admin console with
                    administrator privileges.
                  </Typography>
                  <Typography variant={"body2"} sx={{ mt: 1 }}>
                    Navigate to <b>Core Services → Users → Add User</b>
                  </Typography>
                  <Typography variant={"body2"} sx={{ mt: 1 }}>
                    Create a new user with the following settings:
                  </Typography>
                  <ul>
                    <li>
                      <Typography variant={"body2"}>
                        Set <b>Login Name</b>, <b>Suffix</b>{" "}
                        (cyberark.cloud.###), and <b>Display name</b>
                      </Typography>
                    </li>

                    <li>
                      <Typography variant={"body2"}>
                        Select <b>Generated</b> for Password Type and copy the
                        generated password
                      </Typography>
                    </li>
                    <li>
                      <Typography variant={"body2"}>
                        In Status section, check{" "}
                        <b>"Is OAuth confidential client"</b>
                      </Typography>
                    </li>
                    <li>
                      <Typography variant={"body2"}>
                        Click <b>Create User</b> to create the user
                      </Typography>
                    </li>
                  </ul>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: add_user_image,
                        altText: "Create User",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Box
                    sx={{
                      border: `1px solid ${colors.grey_400}`,
                      backgroundColor: colors.modal_bg,
                      borderRadius: 2,
                      padding: 2,
                    }}
                  >
                    <Typography variant="subtitle1">
                      User Name (Login Name + Suffix){" "}
                      <span style={{ color: "red" }}>*</span>
                    </Typography>{" "}
                    <StyledTextField
                      placeholder="e.g., breezeapi@cyberark.cloud.XXXXX"
                      fullWidth
                      value={formValues?.credentials?.client_id || ""}
                      onChange={(e) => {
                        const newFormValues = {
                          credentials: {
                            ...formValues?.credentials,
                            client_id: e.target.value,
                          },
                        };
                        setFormValues(newFormValues);
                      }}
                      disabled={guideMode}
                      sx={{ mb: 2 }}
                    />
                    <Typography variant="subtitle1">
                      Password <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <StyledTextField
                      placeholder="Your password"
                      fullWidth
                      type="password"
                      value={formValues?.credentials?.client_secret || ""}
                      onChange={(e) => {
                        const newFormValues = {
                          credentials: {
                            ...formValues?.credentials,
                            client_secret: e.target.value,
                          },
                        };
                        setFormValues(newFormValues);
                      }}
                      disabled={guideMode}
                    />
                  </Box>
                </GuidelineStep>

                <GuidelineStep title="Create Role">
                  <Typography variant={"body2"}>
                    Navigate to <b>Core Services → Roles → Add Role</b>
                  </Typography>
                  <Box
                    display="flex"
                    gap={1}
                    alignItems="center"
                    sx={{ mt: 1 }}
                  >
                    <Typography variant={"body2"}>
                      Name the role: BreezeRole
                    </Typography>
                    <CustomCopyButton
                      copyTitle="Role Name"
                      copyText="BreezeRole"
                    />
                  </Box>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: add_role_image,
                        altext: "Create Role",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>

                <GuidelineStep title="Configure Role Members and Permissions">
                  <Typography variant={"body2"}>
                    Click on the <b>BreezeRole</b> you just created.
                  </Typography>

                  <Typography variant={"body2"} sx={{ mt: 1 }}>
                    <b>Add Members:</b>
                  </Typography>
                  <ul>
                    <li>
                      <Typography variant={"body2"}>
                        Click on <b>Members</b> and add the Breeze user you
                        created
                      </Typography>
                    </li>
                  </ul>
                  <Typography variant={"body2"} sx={{ mt: 1 }}>
                    <b>Configure Administrative Rights:</b>
                  </Typography>
                  <ol>
                    <li>
                      <Typography variant={"body2"}>
                        Click on <b>Administrative Rights</b> and click{" "}
                        <b>Add</b>
                      </Typography>
                    </li>
                    <li>
                      <Typography variant={"body2"}>
                        Type "Read" in the search bar to filter for all read
                        rights
                      </Typography>
                    </li>
                    <li>
                      <Typography variant={"body2"}>
                        Select all "Read" rights
                      </Typography>
                    </li>
                    <li>
                      <Typography variant={"body2"}>
                        <b>Note:</b> Make sure to scroll down to select
                        everything
                      </Typography>
                    </li>
                  </ol>
                  <Typography variant={"body2"} sx={{ mt: 1 }}>
                    Click <b>Save</b> to save the role configuration.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: add_rights_image,
                        altext: "Add Administrative Rights",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>

                <GuidelineStep title="Create OAuth2 Client Application">
                  <Typography variant={"body2"}>
                    Navigate to <b>Apps & Widgets → Web Apps → Add Web Apps</b>
                  </Typography>

                  <Typography variant={"body2"} sx={{ mt: 1 }}>
                    <b>Step 1:</b> Click the <b>Custom</b> tab and click{" "}
                    <b>Add</b> next to <b>OAuth2 Client</b>
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: add_OAuth2_Client_image,
                        altext: "Create OAuth2 Client Application",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>

                <GuidelineStep title="Configure Application Settings">
                  <Box display="flex" gap={1} alignItems="center">
                    <Typography variant={"body2"}>
                      Set both <b>Application ID</b> and <b>Name</b> to: Breeze
                    </Typography>

                    <CustomCopyButton
                      copyTitle="Application Name"
                      copyText="Breeze"
                    />
                  </Box>
                  <Typography variant={"body2"} sx={{ mt: 1 }}>
                    <b>Note:</b> The application name is <b>case sensitive</b>
                  </Typography>
                  <Box
                    sx={{
                      border: `1px solid ${colors.grey_400}`,
                      backgroundColor: colors.modal_bg,
                      borderRadius: 2,
                      padding: 2,
                    }}
                  >
                    <Typography variant="subtitle1">
                      Application Name <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 2 }}
                    >
                      Required field - Must match the application name you
                      created (case sensitive)
                    </Typography>
                    <StyledTextField
                      placeholder="Breeze"
                      fullWidth
                      value={formValues?.credentials?.application_name || ""}
                      onChange={(e) => {
                        const newFormValues = {
                          credentials: {
                            ...formValues?.credentials,
                            application_name: e.target.value,
                          },
                        };
                        setFormValues(newFormValues);
                      }}
                      disabled={guideMode}
                    />
                  </Box>
                </GuidelineStep>

                <GuidelineStep title="General Usage Configuration">
                  <Typography variant={"body2"}>
                    Click the <b>General Usage</b> tab:
                  </Typography>
                  <ul>
                    <li>
                      <Typography variant={"body2"}>
                        Select <b>Client ID Type</b> = <b>Confidential</b>
                      </Typography>
                    </li>
                    <li>
                      <Typography variant={"body2"}>
                        Select <b>Must be OAuth Client</b> checkbox
                      </Typography>
                    </li>
                  </ul>
                  <Typography variant={"body2"} sx={{ mt: 1 }}>
                    <b>Copy the Issuer</b> URL from this page and paste it
                    below.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: copy_the_issuer_image,
                        altext: "General Usage Configuration",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Box
                    sx={{
                      border: `1px solid ${colors.grey_400}`,
                      backgroundColor: colors.modal_bg,
                      borderRadius: 2,
                      padding: 2,
                    }}
                  >
                    <Typography variant="subtitle1">
                      Issuer <span style={{ color: "red" }}>*</span>
                    </Typography>

                    <StyledTextField
                      placeholder="e.g., https://aatXXXX.id.cyberark.cloud"
                      fullWidth
                      value={formValues?.credentials?.issuer || ""}
                      onChange={(e) => {
                        const newFormValues = {
                          credentials: {
                            ...formValues?.credentials,
                            issuer: e.target.value,
                          },
                        };
                        setFormValues(newFormValues);
                      }}
                      disabled={guideMode}
                    />
                  </Box>
                </GuidelineStep>

                <GuidelineStep title="Token Configuration">
                  <Typography variant={"body2"}>
                    Click the <b>Tokens</b> tab:
                  </Typography>
                  <ul>
                    <li>
                      <Typography variant={"body2"}>
                        Select <b>"Client Creds"</b>
                      </Typography>
                    </li>
                    <li>
                      <Typography variant={"body2"}>
                        Set <b>Token Type</b> = <b>JwtRS256</b>
                      </Typography>
                    </li>
                    <li>
                      <Typography variant={"body2"}>
                        Set <b>Token Lifespan</b> = <b>10 hours</b>
                      </Typography>
                    </li>
                  </ul>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: set_token_Lifespan_image,
                        altext: "Set Token Lifespan",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>

                <GuidelineStep title="Scope Configuration">
                  <Typography variant={"body2"}>
                    Click the <b>Scope</b> tab:
                  </Typography>
                  <ol>
                    <li>
                      <Typography variant={"body2"}>
                        Click <b>Add</b> and name the scope: <b>Breeze</b>
                      </Typography>
                      <Typography
                        variant={"body2"}
                        sx={{ fontStyle: "italic", mt: 0.5 }}
                      >
                        Note: The scope name is <b>case sensitive</b>
                      </Typography>
                    </li>
                    <li>
                      <Typography variant={"body2"}>
                        Click <b>Add</b> on the <b>Allowed REST APIs</b>
                      </Typography>
                    </li>
                    <li>
                      <Box display="flex" gap={1} alignItems="center">
                        <Typography variant={"body2"}>Type:</Typography>{" "}
                        <b>.*</b>
                        <CustomCopyButton copyTitle="" copyText=".*" />
                      </Box>
                      <Typography
                        variant={"body2"}
                        sx={{ fontStyle: "italic" }}
                      >
                        (a dot and an asterisk)
                      </Typography>
                    </li>
                    <li>
                      <Typography variant={"body2"}>
                        Click <b>Save</b> to save the scope
                      </Typography>
                    </li>
                  </ol>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: add_scope_image,
                        altext: "Add Scope",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Box
                    sx={{
                      border: `1px solid ${colors.grey_400}`,
                      backgroundColor: colors.modal_bg,
                      borderRadius: 2,
                      padding: 2,
                    }}
                  >
                    <Typography variant="subtitle1">
                      Scope Name <span style={{ color: "red" }}>*</span>
                    </Typography>{" "}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 2 }}
                    >
                      Required field - Must match the scope you created (case
                      sensitive)
                    </Typography>
                    <StyledTextField
                      placeholder="Breeze"
                      fullWidth
                      value={formValues?.credentials?.scope || ""}
                      onChange={(e) => {
                        const newFormValues = {
                          credentials: {
                            ...formValues?.credentials,
                            scope: e.target.value,
                          },
                        };
                        setFormValues(newFormValues);
                      }}
                      disabled={guideMode}
                    />
                  </Box>
                </GuidelineStep>

                <GuidelineStep title="Permissions Configuration">
                  <Typography variant={"body2"}>
                    Click the <b>Permissions</b> tab:
                  </Typography>
                  <ol>
                    <li>
                      <Typography variant={"body2"}>
                        Click <b>Add</b>, search for the <b>Breeze user</b> and{" "}
                        <b>BreezeRole</b>, and add both
                      </Typography>
                    </li>
                    <li>
                      <Typography variant={"body2"}>
                        Click <b>Add</b> again to exit the wizard
                      </Typography>
                    </li>
                    <li>
                      <Typography variant={"body2"}>
                        In the Permissions page, find the <b>BreezeRole</b> and{" "}
                        <b>Breeze user</b>
                      </Typography>
                    </li>
                    <li>
                      <Typography variant={"body2"}>
                        Select the checkboxes for: <b>Grant</b>, <b>View</b>,{" "}
                        <b>Manage</b>, <b>Run</b>, <b>Automatically Deploy</b>
                      </Typography>
                    </li>
                    <li>
                      <Typography variant={"body2"}>
                        Click <b>Save</b> to deploy the app
                      </Typography>
                    </li>
                    <li>
                      <Typography variant={"body2"}>
                        Make sure the application is showing{" "}
                        <b>Status: Deployed</b>
                      </Typography>
                    </li>
                  </ol>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: add_permissions_role_user_image,
                        altext: "Add Permissions Role User",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <ScreenshotAccordion
                    images={[
                      {
                        file: deployed_status_image,
                        altext: "Deployed Status",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>

                <GuidelineStep title="Get Your Tenant ID">
                  <Typography variant={"body2"}>
                    To find your Tenant ID:
                  </Typography>
                  <ol>
                    <li>
                      <Typography variant={"body2"}>
                        Click the <b>username icon</b> on the top right
                      </Typography>
                    </li>
                    <li>
                      <Typography variant={"body2"}>
                        Select <b>About</b>
                      </Typography>
                    </li>
                    <li>
                      <Typography variant={"body2"}>
                        Copy the <b>ID of the tenant</b> - this is your
                        Organization ID
                      </Typography>
                    </li>
                  </ol>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: tenant_ID_image,
                        altext: "Get Your Tenant ID",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Box
                    sx={{
                      border: `1px solid ${colors.grey_400}`,
                      backgroundColor: colors.modal_bg,
                      borderRadius: 2,
                      padding: 2,
                    }}
                  >
                    <Typography variant="subtitle1">
                      Tenant ID <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <StyledTextField
                      placeholder="Your Tenant ID"
                      fullWidth
                      value={formValues?.credentials?.org_id || ""}
                      onChange={(e) => {
                        const newFormValues = {
                          credentials: {
                            ...formValues?.credentials,
                            org_id: e.target.value,
                          },
                        };
                        setFormValues(newFormValues);
                      }}
                      disabled={guideMode}
                    />
                  </Box>
                </GuidelineStep>

                <GuidelineStep title="Complete Integration Setup">
                  <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                    <PrimaryButton
                      onClick={() => onSubmit(formValues)}
                      disabled={
                        guideMode ||
                        !formValues?.credentials?.issuer ||
                        !formValues?.credentials?.application_name ||
                        !formValues?.credentials?.scope ||
                        !formValues?.credentials?.client_id ||
                        !formValues?.credentials?.client_secret ||
                        !formValues?.credentials?.org_id
                      }
                    >
                      Connect CyberArk Identity
                    </PrimaryButton>
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default CyberArkIdentityIntegration;
